import { Mesocycle, ExerciseLog, ExerciseLogEntry } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787/api';

let getAccessToken: (() => Promise<string>) | null = null;

export const setAccessTokenGetter = (getter: () => Promise<string>) => {
  getAccessToken = getter;
};

const getAuthHeaders = async (): Promise<HeadersInit> => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (getAccessToken) {
    try {
      const token = await getAccessToken();
      headers['Authorization'] = `Bearer ${token}`;
    } catch (err) {
      console.error('Failed to get access token:', err);
    }
  }

  return headers;
};

const parseMesocycle = (data: any): Mesocycle => ({
  ...data,
  weeks: typeof data.weeks === 'string' ? JSON.parse(data.weeks) : data.weeks,
  active: Boolean(data.active),
});

export const api = {
  mesocycles: {
    async getAll(): Promise<Mesocycle[]> {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/mesocycles`, { headers });
      if (!response.ok) throw new Error('Failed to fetch mesocycles');
      const data = await response.json();
      return data.map(parseMesocycle);
    },

    async getActive(): Promise<Mesocycle[]> {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/mesocycles?active=true`, { headers });
      if (!response.ok) throw new Error('Failed to fetch active mesocycles');
      const data = await response.json();
      return data.map(parseMesocycle);
    },

    async getById(id: string): Promise<Mesocycle> {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/mesocycles/${id}`, { headers });
      if (!response.ok) throw new Error('Failed to fetch mesocycle');
      const data = await response.json();
      return parseMesocycle(data);
    },

    async create(mesocycle: Omit<Mesocycle, 'id'>): Promise<Mesocycle> {
      console.log('Creating mesocycle:', mesocycle);
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/mesocycles`, {
        method: 'POST',
        headers,
        body: JSON.stringify(mesocycle),
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to create mesocycle:', errorText);
        throw new Error('Failed to create mesocycle');
      }
      const data = await response.json();
      console.log('Created mesocycle response:', data);
      const parsed = parseMesocycle(data);
      console.log('Parsed mesocycle:', parsed);
      return parsed;
    },

    async update(id: string, mesocycle: Partial<Mesocycle>): Promise<Mesocycle> {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/mesocycles/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(mesocycle),
      });
      if (!response.ok) throw new Error('Failed to update mesocycle');
      const data = await response.json();
      return parseMesocycle(data);
    },

    async delete(id: string): Promise<void> {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/mesocycles/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (!response.ok) throw new Error('Failed to delete mesocycle');
    },
  },

  exerciseLogs: {
    async create(log: Omit<ExerciseLog, 'id'>): Promise<ExerciseLog> {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/exercise-logs`, {
        method: 'POST',
        headers,
        body: JSON.stringify(log),
      });
      if (!response.ok) throw new Error('Failed to create exercise log');
      return response.json();
    },

    async bulkCreate(logs: ExerciseLogEntry[], mesocycleId: string, weekNumber: number, dayOfWeek: number): Promise<ExerciseLog> {
      const exerciseLog: Omit<ExerciseLog, 'id'> = {
        mesocycleId,
        weekNumber,
        dayOfWeek,
        logs,
        completedAt: new Date().toISOString(),
      };

      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/exercise-logs`, {
        method: 'POST',
        headers,
        body: JSON.stringify(exerciseLog),
      });
      if (!response.ok) throw new Error('Failed to create exercise logs');
      return response.json();
    },

    async getByMesocycleAndSession(mesocycleId: string, weekNumber: number, dayOfWeek: number): Promise<ExerciseLogEntry[]> {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/exercise-logs?mesocycleId=${mesocycleId}&weekNumber=${weekNumber}&dayOfWeek=${dayOfWeek}`,
        { headers }
      );
      if (!response.ok) throw new Error('Failed to fetch exercise logs');
      const data = await response.json();

      // Backend returns flattened log entries directly
      return data;
    },
  },
};